import React from 'react';
import { ProcessForm } from '../uform-types';
import { Form, FormDateField } from '@eas/common-web';
import { noop } from 'lodash';
import {
  FormTextField,
  FormPanel,
  FormAutocomplete,
  useFormSelector,
  FormCheckbox,
  FormFileField,
  FormSelect,
  useStaticListSource,
  FormTextArea,
  FormFileTable,
} from '@eas/common-web';
import { HelpLabel } from '../../../components/help/help-label';
import { UForm, UFormGovAgenda, UFormGovService } from '../../../models';
import { PersonType } from '../../../models/uform';
import { AddressField } from '../../../components/form/address-field/address-field';
import { useUFormGovActAutocomplete } from '../../uform-gov-act/uform-gov-act-api';
import { useUFormGovAgendaAutocomplete } from '../../uform-gov-agenda/uform-gov-agenda-api';
import { useUFormGovServiceAutocomplete } from '../../uform-gov-service/uform-gov-service-api';
import { useOVMAutocomplete } from '../../ovm/ovm-api';
import { uformLabelMapper } from '../../uform/uform-utils';
import { AddressDefaultsContext } from '../../../components/form/address-field/address-field-defaults-context';

export function UFormFields() {
  const {
    agenda,
    service,
    actorProxy,
    originatorPersonType,
    differentContactAddress,
    executiveDeclaration,
  } = useFormSelector(
    (
      values: UForm & {
        agenda: UFormGovAgenda | null;
        service: UFormGovService | null;
        representedPerson: {
          differentContactAddress: boolean;
        };
        actor: UForm['actor'] & { executiveDeclaration: boolean };
      }
    ) => ({
      act: values.act,
      agenda: values.agenda,
      executiveDeclaration: values.actor?.executiveDeclaration,
      service: values.service,
      actorProxy: values.actor?.proxy,
      originatorPersonType: values.representedPerson?.type,
      differentContactAddress:
        values.representedPerson?.differentContactAddress,
    })
  );

  const agendas = useUFormGovAgendaAutocomplete();
  const services = useUFormGovServiceAutocomplete(agenda?.id || '');
  const acts = useUFormGovActAutocomplete(service?.id || '');
  const ovms = useOVMAutocomplete(service?.id || '');

  const representedPersonOptions = useStaticListSource([
    { id: PersonType.NATURAL_PERSON, name: 'Fyzická osoba' },
    { id: PersonType.BUSINESS_PERSON, name: 'Osoba s IČO' },
    { id: PersonType.FOREIGN_PERSON, name: 'Zahraniční osoba' },
  ]);

  return (
    <>
      <AddressDefaultsContext.Provider
        value={{
          addressPlace: { required: false },
          cityDistrict: { required: false },
          country: { required: false },
          district: { required: false },
          houseNumber: { required: false },
          houseNumberType: { required: false },
          municipality: { required: false },
          municipalityPart: { required: false },
          orientationNumber: { required: false },
          orientationNumberMark: { required: false },
          region: { required: false },
          street: { required: false },
          zip: { required: false },
        }}
      >
        <FormPanel label="Informace o službě">
          <FormAutocomplete
            name="agenda"
            label={<HelpLabel label="Agenda" code="UFORM_AGENDA" />}
            source={agendas}
            labelMapper={uformLabelMapper}
          />
          <FormAutocomplete
            name="service"
            label={<HelpLabel label="Služba" code="UFORM_SERVICE" />}
            source={services}
            labelMapper={uformLabelMapper}
          />
          <FormAutocomplete
            name="act"
            label={<HelpLabel label="Úkon" code="UFORM_ACT" />}
            source={acts}
            labelMapper={uformLabelMapper}
          />
          <FormAutocomplete
            name="recipient.authority"
            label={<HelpLabel label="OVM příjemce" code="UFORM_RECIPIENT" />}
            source={ovms}
          />
          <FormTextField
            name="recipient.databox"
            label={<HelpLabel label="Datová schránka" code="UFORM_ISDS" />}
          />
          <FormCheckbox
            name="recipient.isCustomDatabox"
            label={
              <HelpLabel
                label="Chci zadat odlišnou datovou schránku"
                code="UFORM_IS_CUSTOM_ISDS"
              />
            }
          />
          <FormTextField
            name="recipient.customDatabox"
            label={
              <HelpLabel
                label="Vlastní datová schránka"
                code="UFORM_CUSTOM_ISDS"
              />
            }
          />
          <FormTextField
            name="recipient.department"
            label={
              <HelpLabel label="K rukám / Odbor" code="UFORM_DEPARTMENT" />
            }
          />
        </FormPanel>
        <FormPanel label="Osoba činící úkon">
          <FormTextField
            name="actor.firstName"
            label={<HelpLabel label="Jméno" code="UFORM_FIRST_NAME" />}
          />
          <FormTextField
            name="actor.lastName"
            label={<HelpLabel label="Příjmení" code="UFORM_LAST_NAME" />}
          />
          <FormDateField
            name="actor.birthDate"
            label={<HelpLabel label="Datum narození" code="UFORM_BIRTH_DATE" />}
          />
          <FormTextField
            name="actor.idNumber"
            label={<HelpLabel label="Číslo OP" code="UFORM_BIRTH_ID_NUMBER" />}
          />
          <FormTextField
            name="actor.databox"
            label={
              <HelpLabel label="Datová schránka" code="UFORM_BIRTH_DATABOX" />
            }
          />
          <FormSelect
            name="actor.preferredResponseChannel"
            label={
              <HelpLabel
                label="Preferovaný kanál pro odpověď "
                code="UFORM_PREFERRED_RESPONSE_CHANNEL"
              />
            }
            valueIsId={true}
            // eslint-disable-next-line react-hooks/rules-of-hooks
            source={useStaticListSource([
              { id: 'ISDS', name: 'Datová schránka' },
              { id: 'MAIL', name: 'Pošta' },
            ])}
          />
          <FormTextField
            name="actor.email"
            label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
          />
          <FormTextField
            name="actor.phone"
            label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
          />
          <AddressField
            name="actor.address"
            disableForeignCountry={true}
            disabled={true}
          />
          <FormCheckbox
            name="actor.proxy"
            label={
              <HelpLabel
                label="Úkon prováděn v zastoupení"
                code="UFORM_ACTING_ON_BEHALF"
              />
            }
          />
          {actorProxy && (
            <FormFileField
              name="actor.powerOfAttorney"
              label={
                <HelpLabel
                  label="Plná moc"
                  code="UFORM_POWER_OF_ATTORNEY"
                  required={!executiveDeclaration}
                />
              }
              accept={['.pdf']}
            />
          )}
          {actorProxy &&
            (originatorPersonType === PersonType.BUSINESS_PERSON ||
              originatorPersonType === PersonType.FOREIGN_PERSON) && (
              <FormCheckbox
                name="actor.executiveDeclaration"
                label={
                  <HelpLabel
                    label="Čestně prohlašuji, že jsem jednatelem zastupované osoby"
                    code="UFORM_EXECUTIVE_DECLARATION"
                  />
                }
              />
            )}
          <FormSelect
            name="representedPerson.type"
            label={
              actorProxy ? (
                <HelpLabel
                  label="Podání je činěno za"
                  code="UFORM_SUBMISSION_FOR"
                  required
                />
              ) : (
                <HelpLabel label="Podání činí" code="UFORM_SUBMISSION_FOR" />
              )
            }
            valueIsId={true}
            source={representedPersonOptions}
          />
        </FormPanel>

        {originatorPersonType === PersonType.NATURAL_PERSON && (
          <>
            <FormPanel label="Fyzická osoba">
              <FormTextField
                name="representedPerson.firstName"
                label={<HelpLabel label="Jméno" code="UFORM_FIRST_NAME" />}
              />
              <FormTextField
                name="representedPerson.lastName"
                label={<HelpLabel label="Příjmení" code="UFORM_LAST_NAME" />}
              />
              <FormTextField
                name="representedPerson.birthDate"
                label={
                  <HelpLabel label="Datum narození" code="UFORM_BIRTH_DATE" />
                }
              />
              <FormTextField
                name="representedPerson.idNumber"
                label={
                  <HelpLabel
                    label="Číslo OP"
                    code="UFORM_INDIVIDUAL_ID_NUMBER"
                  />
                }
              />
              <FormTextField
                name="representedPerson.email"
                label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
              />
              <FormTextField
                name="representedPerson.phone"
                label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
              />
              <FormTextField
                name="representedPerson.databox"
                label={
                  <HelpLabel
                    label="Datová schránka zmocnitele"
                    code="UFORM_DATA_BOX"
                  />
                }
              />
              <AddressField name="representedPerson.address" />
              <FormCheckbox
                name="representedPerson.differentContactAddress"
                label={
                  <HelpLabel
                    label="Kontaktní adresa je jiná než adresa trvalého pobytu"
                    code="UFORM_DIFFERENT_CONTACT_ADDRESS"
                  />
                }
              />
            </FormPanel>
            {differentContactAddress && (
              <FormPanel label="Kontaktní adresa">
                <AddressField
                  name="representedPerson.contactAddress"
                  disableForeignCountry={true}
                />
              </FormPanel>
            )}
          </>
        )}

        {originatorPersonType === PersonType.BUSINESS_PERSON && (
          <FormPanel label="Osoba s IČO">
            <FormTextField
              name="representedPerson.ico"
              label={<HelpLabel label="IČO" code="UFORM_ICO" />}
            />
            <FormTextField
              name="representedPerson.companyName"
              label={<HelpLabel label="Název" code="UFORM_COMPANY_NAME" />}
            />
            <FormTextField
              name="representedPerson.databox"
              label={
                <HelpLabel label="Datová schránka" code="UFORM_DATA_BOX" />
              }
            />
            <FormTextField
              name="representedPerson.email"
              label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
            />
            <FormTextField
              name="representedPerson.phone"
              label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
            />
            <AddressField
              name="representedPerson.contactAddress"
              disableForeignCountry={true}
            />
          </FormPanel>
        )}

        {originatorPersonType === PersonType.FOREIGN_PERSON && (
          <FormPanel label="Zahraniční osoba">
            <FormTextField
              name="representedPerson.ico"
              label={
                <HelpLabel
                  label="Zahraniční identifikátor (obdoba IČO)"
                  code="UFORM_FOREIGN_IDENTIFIER"
                />
              }
            />
            <FormTextField
              name="representedPerson.companyName"
              label={
                <HelpLabel label="Název" code="UFORM_FOREIGN_COMPANY_NAME" />
              }
            />
            <FormTextField
              name="representedPerson.email"
              label={<HelpLabel label="E-mail" code="UFORM_EMAIL" />}
            />
            <FormTextField
              name="representedPerson.phone"
              label={<HelpLabel label="Telefonní číslo" code="UFORM_PHONE" />}
            />
            <AddressField name="representedPerson.address" />

            <FormPanel label="Kontaktní adresa na území ČR">
              <AddressField
                name="representedPerson.contactAddress"
                disableForeignCountry={true}
              />
            </FormPanel>
          </FormPanel>
        )}

        <FormPanel label="Podání">
          <FormTextArea
            name="submission.text"
            label={
              <HelpLabel
                label="Popis žádosti"
                code="UFORM_APPLICATION_DESCRIPTION"
                required
              />
            }
            labelOptions={{
              hide: true,
            }}
            minRows={8}
          />
        </FormPanel>
        <FormPanel label="Přílohy">
          <FormFileTable
            name="submission.attachments"
            label="Přílohy"
            labelOptions={{ hide: true }}
          />
        </FormPanel>
      </AddressDefaultsContext.Provider>
    </>
  );
}

export function StepSummaryContent({ allValues }: { allValues: ProcessForm }) {
  return (
    <>
      <Form editing={false} onSubmit={noop} initialValues={allValues}>
        <UFormFields />
      </Form>

      <FormCheckbox
        name="informationValidityDeclaration"
        disabled={false}
        labelOptions={{ hide: true }}
        label=""
        after={
          <div style={{ marginLeft: 8 }}>
            <HelpLabel
              label="Prohlašuji, že uvedené údaje a přílohy jsou správné a odpovídají skutečnosti."
              code="UFORM_DECLARE_INFORMATION_VALIDITY"
              required
            />
          </div>
        }
      />

      <FormCheckbox
        name="gdprAgreement"
        disabled={false}
        labelOptions={{ hide: true }}
        label=""
        after={
          <div style={{ marginLeft: 8 }}>
            <HelpLabel
              required
              label="Beru na vědomí podmínky a informace o zpracování osobních údajů
                (GDPR)."
              code="UFORM_GDPR_AGREEMENT"
              labelJsx={
                <div>
                  Beru na vědomí podmínky a informace o zpracování osobních
                  údajů (GDPR). Plné znění zásad MŽP webových stránkách viz{' '}
                  <a
                    href="https://mzp.gov.cz/cz/kontakty/ochrana-osobnich-udaju"
                    rel="noreferrer"
                    target="_blank"
                  >
                    https://mzp.gov.cz/cz/kontakty/ochrana-osobnich-udaju
                  </a>
                </div>
              }
            />
          </div>
        }
      />
    </>
  );
}
